import intl from 'react-intl-universal';
import { TextArea, Typography } from '@getsynapse/design-system';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import {
  deletePropertiesComment,
  updatePropertiesComment,
  postPropertiesComment,
} from 'state/RequestComments/requestCommentsSlice';
import { exists } from '../helpers';
import { IntakeQuestions } from '../IntakeQuestions';
import classnames from 'classnames';
import { REQUEST_PROPERTIES, REQUEST_SECTIONS } from 'utils/constants';
import {
  newPropertyCommentType,
  PropertyCommentType,
  Request,
  RequestQuestion,
  UpdateReqData,
  ProjectCommentMentionsData,
} from 'utils/customTypes';
import Comments from 'Organisms/Comments/Comments';
import getPropertyComments from 'Hooks/getPropertyComments';
import { useMemo } from 'react';
import { selectIsActiveRequestAForm } from 'state/ActiveRequest/activeRequestSlice';

const RequestDetails = ({
  requestData,
  requestQuestionsData,
  showComment = false,
  propertiesComments = {},
  disableFields = false,
  setUpdatedReqData = () => {},
  questionIdParam = '',
  propertyNameParam = '',
}: {
  requestQuestionsData: RequestQuestion[];
  requestData: Request;
  showComment?: boolean;
  setUpdatedReqData?: React.Dispatch<React.SetStateAction<UpdateReqData>>;
  propertiesComments?: {
    [key: string]: PropertyCommentType[];
  };
  disableFields?: boolean;
  questionIdParam?: string;
  propertyNameParam?: string;
}) => {
  const dispatch = useDispatch();
  const requestId = requestData.id!;

  const isForm = useSelector(selectIsActiveRequestAForm);

  const questions = useMemo<RequestQuestion[]>(() => {
    if (exists(requestQuestionsData)) {
      return requestQuestionsData.filter(
        (question: { section: string }) =>
          question && question.section === REQUEST_SECTIONS.REQUEST_DETAILS
      );
    }

    return [];
  }, [requestQuestionsData]);

  const onChangeDescription = debounce((value: string) => {
    setUpdatedReqData((prevState: UpdateReqData) => {
      return {
        ...prevState,
        requestAttributes: {
          ...prevState.requestAttributes,
          description: value,
        },
      };
    });
  }, 500);

  const onDeleteComment = (commentId: string, propertykey: string) => {
    dispatch(deletePropertiesComment({ commentId, propertykey }));
  };

  const onEditComment = ({
    commentId,
    content,
    mentionsData,
  }: {
    commentId: string;
    content: string;
    mentionsData: ProjectCommentMentionsData;
  }) => {
    dispatch(
      updatePropertiesComment({ commentId, message: content, mentionsData })
    );
  };

  const onCreateComment = (
    content: string,
    mentionsData: ProjectCommentMentionsData,
    propertykey: string
  ) => {
    const newMessage: newPropertyCommentType = {
      message: content,
      isBaseComment: false,
      requestId: requestId,
      requestProperty: propertykey,
      userId: '',
      data: { mentions: mentionsData },
    };
    dispatch(postPropertiesComment(newMessage));
  };

  return (
    <div>
      <Typography variant='h5' className='mt-8'>
        {intl.get('REQUEST_PAGE.REQUEST_DETAILS.TITLE')}
      </Typography>
      <Typography variant='caption' className='block mb-4 text-neutral-light'>
        {intl.get('REQUEST_PAGE.REQUEST_DETAILS.CAPTION')}
      </Typography>

      <div className='flex flex-col'>
        <div className='grid gap-y-6 gap-x-14 grid-cols-2'>
          <div>
            <div className={classnames('flex', 'justify-between')}>
              <Typography
                variant='label'
                weight='medium'
                data-cy='label__request-description'
                className={classnames('mb-1.5', {
                  'text-neutral-light': disableFields && !isForm,
                })}
              >
                {intl.get(
                  'REQUEST_PAGE.REQUEST_DETAILS.FIELDS.REQUEST_DESCRIPTION'
                )}
              </Typography>
              {showComment && (
                <Comments
                  comments={getPropertyComments(
                    propertiesComments,
                    REQUEST_PROPERTIES.REQUEST_DESC
                  )}
                  onEdit={onEditComment}
                  onDelete={(commentId: string) =>
                    onDeleteComment(commentId, REQUEST_PROPERTIES.REQUEST_DESC)
                  }
                  onCreate={(content, mentionsData) =>
                    onCreateComment(
                      content,
                      mentionsData,
                      REQUEST_PROPERTIES.REQUEST_DESC
                    )
                  }
                  isPopupOpen={
                    propertyNameParam === REQUEST_PROPERTIES.REQUEST_DESC
                  }
                  testId='description'
                />
              )}
            </div>
            <TextArea
              textAreaProps={{
                placeholder: intl.get(
                  'REQUEST_PAGE.REQUEST_DETAILS.FIELDS.REQUEST_DESCRIPTION_PLACEHOLDER'
                ),
                defaultValue: isForm ? '' : requestData.description,
                'data-testid': 'field__request-description',
              }}
              onChange={(e) => onChangeDescription(e.target.value)}
              disabled={disableFields}
            />
          </div>
        </div>

        <div className='grid gap-y-6 gap-x-14 grid-cols-2'>
          {questions && (
            <IntakeQuestions
              questions={questions}
              requestId={requestId}
              showComment={showComment}
              setUpdatedReqData={setUpdatedReqData}
              disableFields={disableFields}
              questionIdParam={questionIdParam}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestDetails;
